import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu as MenuIcon, X, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { RESTAURANT } from "../data/site";
import { scrollToId } from "../lib/scroll";

const SECTION_LINKS = [
  { label: "Menu", id: "menu" },
  { label: "Catering", id: "catering" },
  { label: "Hours", id: "hours" },
  { label: "Reviews", id: "reviews" },
  { label: "Location", id: "location" },
];

export const Navbar = ({ forceSolid = false }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = forceSolid || scrolled;

  const goSection = (id) => {
    setOpen(false);
    if (pathname !== "/") navigate(`/#${id}`);
    else scrollToId(id);
  };
  const goHome = () => {
    setOpen(false);
    navigate("/");
  };
  const goStory = () => {
    setOpen(false);
    navigate("/about");
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        solid
          ? "bg-cream/90 backdrop-blur-md border-b border-gold/20 py-3 shadow-[0_4px_20px_rgba(122,28,34,0.05)]"
          : "bg-transparent py-5"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <button data-testid="nav-logo" onClick={goHome} className="focus:outline-none">
          <Logo variant={solid ? "dark" : "light"} />
        </button>

        <div className="hidden lg:flex items-center gap-6 xl:gap-8">
          <button
            data-testid="nav-link-about"
            onClick={goStory}
            className={`font-body text-sm font-semibold uppercase tracking-widest transition-colors duration-300 hover:text-gold ${
              solid ? "text-charcoal" : "text-cream"
            }`}
          >
            Our Story
          </button>
          {SECTION_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => goSection(l.id)}
              className={`font-body text-sm font-semibold uppercase tracking-widest transition-colors duration-300 hover:text-gold ${
                solid ? "text-charcoal" : "text-cream"
              }`}
            >
              {l.label}
            </button>
          ))}
          <a
            data-testid="nav-call-btn"
            href={RESTAURANT.phoneHref}
            className="inline-flex items-center gap-2 bg-burgundy text-cream px-5 py-2.5 rounded-full font-body text-sm font-semibold tracking-wide hover:bg-burgundy-dark transition-all duration-300 hover:scale-[1.03]"
          >
            <Phone size={15} /> Call
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className={`lg:hidden p-2 rounded-full transition-colors ${solid ? "text-charcoal" : "text-cream"}`}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <MenuIcon size={26} />}
        </button>
      </nav>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-cream border-t border-gold/20 shadow-xl">
          <div className="px-6 py-4 flex flex-col">
            <button
              data-testid="mobile-nav-link-about"
              onClick={goStory}
              className="text-left py-3 font-body text-base font-semibold uppercase tracking-widest text-charcoal border-b border-beige hover:text-gold transition-colors"
            >
              Our Story
            </button>
            {SECTION_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-nav-link-${l.id}`}
                onClick={() => goSection(l.id)}
                className="text-left py-3 font-body text-base font-semibold uppercase tracking-widest text-charcoal border-b border-beige last:border-0 hover:text-gold transition-colors"
              >
                {l.label}
              </button>
            ))}
            <a
              href={RESTAURANT.phoneHref}
              className="mt-4 inline-flex items-center justify-center gap-2 bg-burgundy text-cream px-5 py-3 rounded-full font-body text-sm font-semibold tracking-wide"
            >
              <Phone size={16} /> {RESTAURANT.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
